import type { Maybe } from '@adornis/base/utilTypes.js';

export interface HasRolesOptions {
  user: { roles: Maybe<{ name: Maybe<string>; contexts?: Maybe<string[]> }[]> };
  roles: string[];
  /** Whether the user needs 'any' or 'all' of the given roles. Default: 'any'. */
  mode?: 'any' | 'all';
  /** Optional list of contexts to filter roles by before checking. */
  contexts?: string[];
  /** Whether the role must match 'any' or 'all' of the given contexts. Default: 'any'. */
  contextMode?: 'any' | 'all';
}

/**
 * Checks if the user has the given roles. This should not be used to ask for specific permissions but instead to use the roles if a person has a specific state in the system.
 * When `contexts` is provided, only roles that match the context filter are considered.
 * @returns True if the user has the given roles (within the given contexts, if provided)
 */
export function hasRoles({ user, roles, mode = 'any', contexts, contextMode = 'any' }: HasRolesOptions): boolean {
  const relevantRoles = user.roles?.filter(r => {
    if (!contexts) return true;
    const roleContexts = r.contexts ?? [];
    if (contextMode === 'any') return contexts.some(ctx => roleContexts.includes(ctx));
    return contexts.every(ctx => roleContexts.includes(ctx));
  });

  if (mode === 'any') return roles.some(role => relevantRoles?.find(r => r.name === role));
  return roles.every(role => relevantRoles?.find(r => r.name === role));
}
